import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { APP_TIMEZONE } from '@/lib/timezone';
import {
  JobManagementPage as JobManagementPageComponent,
  type JobLifecycleAction,
  type JobManagementDialog,
  type JobManagementSection,
  type JobManagementSectionId,
  type JobOperationalAction,
  type JobStatus,
} from './JobManagementPage.component';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { buildJobSections } from '@/features/jobs/job-shared/job-sections';
import { JobMutationError } from '@/features/jobs/job-shared/job-edit-mutation';
import {
  useDuplicateJob,
  useTransitionJobStatus,
} from '@/features/jobs/jobs-list/jobs-list-mutations';
import { programByIdQueryOptions } from '@/features/programs/program-edit/program-by-id-query';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';

/** RN-08 — plataforma externa de exportação, via link direto (sem auth
 *  integrada e sem passar a vaga como parâmetro nesta versão). */
const EXPORT_PLATFORM_URL = 'https://rank.eureca.me/';

/** RN-12 — Sonner exibido por 8 segundos com botão "Fechar". */
const SONNER_DURATION_MS = 8000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Prazo do card: "Prazo de inscrição: 24/06 até 24/07/2026" — dia civil lido em
 * America/Sao_Paulo (superfície de autoria do consultor; ver
 * date-timezone-standard.md). Início sem ano (Figma), fim com ano.
 */
const START_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  timeZone: APP_TIMEZONE,
});
const END_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: APP_TIMEZONE,
});

function formatInstant(value: string | null | undefined, format: Intl.DateTimeFormat) {
  if (!value) return null;
  // Aceita tanto ISO quanto o timestamptz cru do Postgres ("2026-06-01 03:00:00+00")
  // — mesmo racional do `toApiInstant` (EU-402).
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return format.format(new Date(ms));
}

/** Deep-link de cada seção do painel "Dados" para o fluxo de edição (RN-11). */
const SECTION_ROUTES = {
  'general-info': '/vagas/$jobId/editar/informacoes-gerais',
  flow: '/vagas/$jobId/editar/fluxo',
  jobs: '/vagas/$jobId/editar/vagas',
  'work-conditions': '/vagas/$jobId/editar/condicoes-de-trabalho',
  compensation: '/vagas/$jobId/editar/remuneracao',
  education: '/vagas/$jobId/editar/formacao',
  requirements: '/vagas/$jobId/editar/requisitos',
  faqs: '/vagas/$jobId/editar/perguntas-frequentes',
} as const satisfies Record<JobManagementSectionId, string>;

export default function JobManagementPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({ from: '/vagas/$jobId' });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const job = jobQuery.data;

  // Janela de inscrição + logo vêm do PROGRAMA vinculado (EU-427 — a vaga não
  // tem datas próprias; a empresa vem de Job → Program → Company).
  const programId = job?.programId ?? '';
  const programQuery = useQuery({
    ...programByIdQueryOptions(programId),
    enabled: UUID_RE.test(programId),
  });
  const program = programQuery.data;

  const duplicateJob = useDuplicateJob();
  const transitionStatus = useTransitionJobStatus();
  const [activeDialog, setActiveDialog] = useState<JobManagementDialog>(null);
  const [actionInFlight, setActionInFlight] = useState(false);

  const status: JobStatus = (job?.status as JobStatus | undefined) ?? 'rascunho';
  const sections: JobManagementSection[] = useMemo(
    () =>
      buildJobSections(job).map(({ id, title, description, optional }) => ({
        // No painel "Dados" o ticket/Figma nomeia a seção de posições como
        // "Áreas de atuação" (o hub de edição a chamava de "Vagas"). A
        // `description` (Preenchido / contadores / "Seção ainda não
        // preenchida") vem junto — status de preenchimento herdado do hub.
        id,
        title: id === 'jobs' ? 'Áreas de atuação' : title,
        description,
        optional,
      })),
    [job]
  );

  const deadline = useMemo(() => {
    const raw = program as
      | { startApplying?: string | null; endApplying?: string | null }
      | undefined;
    const start = formatInstant(raw?.startApplying, START_FORMAT);
    const end = formatInstant(raw?.endApplying, END_FORMAT);
    if (!start || !end) return null;
    return { start, end };
  }, [program]);

  async function handleDuplicate() {
    if (actionInFlight) return;
    setActionInFlight(true);
    try {
      const cloned = await duplicateJob.mutateAsync({ id: jobId });
      toast('Vaga duplicada.', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      // A cópia (rascunho) abre na Gestão de vaga dela.
      await navigate({
        to: '/vagas/$jobId',
        params: { jobId: cloned.id },
      });
    } catch (err) {
      const errStatus = err instanceof JobMutationError ? err.status : 0;
      if (errStatus === 404) {
        toast.error('Vaga não encontrada.');
      } else if (errStatus === 401 || errStatus === 403) {
        toast.error('Você não tem permissão para duplicar essa vaga.');
      } else {
        toast.error('Não foi possível duplicar a vaga. Tente novamente.');
      }
    } finally {
      setActionInFlight(false);
    }
  }

  function handleLifecycleAction(action: JobLifecycleAction) {
    if (action === 'duplicate') {
      void handleDuplicate();
      return;
    }
    setActiveDialog(action);
  }

  /**
   * Transição única de status. O Sonner de sucesso segue RN-12: 8 segundos +
   * botão "Fechar". A página permanece aberta — a invalidação da mutation
   * atualiza badge e matriz de ações no lugar.
   */
  async function transition(target: 'encerrada' | 'cancelada', successMessage: string) {
    if (actionInFlight) return;
    setActionInFlight(true);
    try {
      await transitionStatus.mutateAsync({ id: jobId, status: target });
      setActiveDialog(null);
      toast(successMessage, {
        duration: SONNER_DURATION_MS,
        action: { label: 'Fechar', onClick: () => undefined },
      });
    } catch (err) {
      const errStatus = err instanceof JobMutationError ? err.status : 0;
      if (errStatus === 422) {
        toast.error('Essa vaga não pode mais ser alterada.');
      } else if (errStatus === 404) {
        toast.error('Vaga não encontrada.');
      } else if (errStatus === 401 || errStatus === 403) {
        toast.error('Você não tem permissão para essa ação.');
      } else {
        toast.error('Não foi possível atualizar o status. Tente novamente.');
      }
    } finally {
      setActionInFlight(false);
    }
  }

  function handleOperationalAction(action: JobOperationalAction) {
    if (action === 'export') {
      // RN-08 — abre a plataforma externa em nova aba, sem parâmetro de vaga.
      window.open(EXPORT_PLATFORM_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    if (action === 'move') {
      // RN-09 — porta de entrada do fluxo de movimentação (EU-551).
      void navigate({ to: '/vagas/$jobId/movimentar', params: { jobId } });
      return;
    }
    if (action === 'reject') {
      // RN-10 — porta de entrada do fluxo de reprovação (EU-552).
      void navigate({ to: '/vagas/$jobId/reprovar', params: { jobId } });
    }
    // 'numbers' nunca chega aqui — o card é sempre desabilitado (RN-07).
  }

  function handleSectionOpen(sectionId: JobManagementSectionId) {
    void navigate({
      to: SECTION_ROUTES[sectionId],
      params: { jobId },
    });
  }

  if (jobQuery.isLoading && !actionInFlight) {
    return (
      <div
        data-testid="job-management-loading"
        className="flex min-h-dvh items-center justify-center bg-card"
      >
        <div className="flex flex-col gap-4" aria-hidden>
          <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
          <div className="h-40 w-[420px] animate-pulse rounded-xl bg-muted" />
          <div className="h-24 w-[420px] animate-pulse rounded-xl bg-muted" />
        </div>
      </div>
    );
  }

  if (jobQuery.isError && !actionInFlight) {
    const fetchStatus = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (fetchStatus === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
      return null;
    }
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-card">
        <p className="text-lg font-medium text-foreground">Não foi possível carregar a vaga</p>
        <button
          type="button"
          data-testid="job-management-error-retry"
          onClick={() => void jobQuery.refetch()}
          className="rounded-md border border-input px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <JobManagementPageComponent
      status={status}
      name={job?.name ?? ''}
      programName={job?.programName ?? ''}
      deadline={deadline}
      companyLogoUrl={program?.company?.logoUrl ?? null}
      companyName={program?.company?.name ?? null}
      sections={sections}
      onLifecycleAction={handleLifecycleAction}
      onOperationalAction={handleOperationalAction}
      onSectionOpen={handleSectionOpen}
      onBack={() => void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH })}
      activeDialog={activeDialog}
      onDialogOpenChange={(open) => {
        if (!actionInFlight && !open) setActiveDialog(null);
      }}
      onConfirmEnd={() => void transition('encerrada', 'Vaga encerrada')}
      onConfirmCancel={() => void transition('cancelada', 'Vaga cancelada')}
      isActionInFlight={actionInFlight}
    />
  );
}
